import React, { FC, ReactNode, useMemo, useState } from 'react';
import { useGetTransformServerGroups } from '../../../api/serverGroup';
import {
  Button,
  ComboFilter,
  CopyButton,
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterListSelector,
  ExtendedTableColumn,
  FlexGroup,
  Flyout,
  Input,
  KeyValueList,
  Loading,
  Popover,
  Select,
  TableViewLayout,
  Text,
  useTableFilters,
  useTectonTheme,
} from '@lithos';
import { useParams } from 'react-router-dom';
import { Avatar, Empty, FlexItem, Link, Table } from '@lithos';
import { Status, TransformServerGroup } from '../../../types/tecton_proto/servergroupservice/server_group_service';
import ServerGroupStatusBadge from './ServerGroupStatusBadge';
import DateCell from '../fcoListView/DateCell';
import { Routes as AppRoutes } from '../../../core/routes';
import { useListEnvironments } from '../../../api/compute';
import { ListRemoteEnvironmentsResponse } from '../../../types/tecton_proto/remoteenvironmentservice/remote_environment_service';
import { defaultDateState } from '../fcoListView/utils';
import { uniq } from 'lodash';
import { getFilteredItems, isInDateRange, tableStringSortParameters } from '@lithos/TableUtils';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';

function renderDependencyList(dependencies: string | undefined): ReactNode {
  if (!dependencies) return 'n/a';
  return (
    dependencies
      .match(/^[a-zA-Z0-9_.-]+==[\w.-]+/gm) // Extract all package==version lines
      ?.map((dep, idx) => <div key={idx}>{dep}</div>) || 'n/a'
  );
}

const ColumnsPopover = ({ columnsSelector }: { columnsSelector: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Popover
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      body={columnsSelector}
      trigger={<Button type="secondary" label="Columns" icon="Columns" testId="ingest-server-groups-columns-button" />}
    />
  );
};

export const EnvironmentFlyout: FC<{ environment: string; closeCallback: () => void }> = ({
  environment,
  closeCallback,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const { data, isLoading } = useListEnvironments();

  return (
    <Flyout
      isOpen={isOpen}
      setIsOpen={() => {
        closeCallback();
        setIsOpen(false);
      }}
      content={isLoading ? <Loading /> : <EnvironmentFlyoutContent environment={environment} environmentData={data} />}
      title={environment}
    />
  );
};

const EnvironmentFlyoutContent: FC<{
  environment: string;
  environmentData: ListRemoteEnvironmentsResponse | undefined;
}> = ({ environment, environmentData }) => {
  const matchingEnvironment = environmentData?.remote_environments?.find((env) => env.name === environment);

  const items = [
    {
      title: 'Name',
      content: matchingEnvironment?.name,
    },
    {
      title: 'ID',
      content: matchingEnvironment?.id,
    },
    {
      title: 'Status',
      content: matchingEnvironment?.status?.replace('REMOTE_ENVIRONMENT_STATUS_', ''),
    },
    {
      title: 'Type',
      content: matchingEnvironment?.type?.replace('REMOTE_ENVIRONMENT_TYPE_', ''),
    },
    {
      title: 'Tecton Runtime Version',
      content: matchingEnvironment?.sdk_version ?? '-',
    },
    {
      title: 'Description',
      content: matchingEnvironment?.description ?? '-',
    },
    {
      title: 'Created At',
      content: matchingEnvironment?.created_at
        ? dateTimeFormat(new Date(matchingEnvironment?.created_at), timeZone.UTC)
        : '-',
    },
    {
      title: 'Last Updated At',
      content: matchingEnvironment?.updated_at
        ? dateTimeFormat(new Date(matchingEnvironment.updated_at), timeZone.UTC)
        : '-',
    },
  ];

  const pipDependencies = [
    {
      title: 'Pip Dependencies',
      content: renderDependencyList(matchingEnvironment?.resolved_requirements),
    },
  ];

  return (
    <>
      <KeyValueList items={items} />
      <KeyValueList items={pipDependencies} />
    </>
  );
};

const SearchInput: FC<{
  hasAnyFilters: boolean;
  activeFilterCount: number;
  handleResetFilters: () => void;
  onSearchInputChange: (value: string) => void;
  categories: ComboFilterCategory[];
}> = ({ hasAnyFilters, activeFilterCount, handleResetFilters, onSearchInputChange, categories }) => {
  const [selectedCategory, setSelectedCategory] = useState<ComboFilterCategory | undefined>(undefined);
  const [searchQuery, _] = useState<string | undefined>(undefined);

  return (
    <FlexGroup padding="0">
      <Input
        value={searchQuery}
        onChange={onSearchInputChange}
        icon="Search"
        placeholder="Search by name or ID"
        type="string"
        size="s"
        disabled={false}
        testId={'search-transform-server-groups-table'}
      />
      <ComboFilter
        icon={'Filters'}
        hasAnyActiveFilters={hasAnyFilters}
        activeFilterCount={activeFilterCount}
        resetFilters={handleResetFilters}
        categories={categories}
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        testId={'filter-transform-server-groups-table'}
      />
    </FlexGroup>
  );
};

enum TransformServerGroupsTableColumnKeys {
  STATUS,
  NAME,
  ENVIRONMENT,
  ID,
  OWNER,
  CREATED_AT,
  LAST_UPDATED_AT,
}

interface TransformServerGroupsTableFilterState {
  Status: string[];
  Name: string[];
  Environment: string[];
  Owner: string[];
  Created: [Date, Date];
  'Last Updated': [Date, Date];
}

const TransformServerGroupsTableContent: FC<{ serverGroups: TransformServerGroup[] }> = ({ serverGroups }) => {
  const { workspace } = useParams();
  const { theme } = useTectonTheme();
  const [flyoutContent, setFlyoutContent] = useState<string | undefined>(undefined);

  const defaultFilterState: TransformServerGroupsTableFilterState = useMemo(() => {
    return {
      Status: [],
      Name: [],
      Environment: [],
      Owner: [],
      Created: defaultDateState(serverGroups, 'created_at').map((date) => new Date(date)) as [Date, Date],
      'Last Updated': defaultDateState(serverGroups, 'updated_at').map((date) => new Date(date)) as [Date, Date],
    };
  }, [serverGroups]);

  const allValidOptions = useMemo(() => {
    return {
      [TransformServerGroupsTableColumnKeys.STATUS]: Object.values(Status),
      [TransformServerGroupsTableColumnKeys.NAME]: serverGroups.map((serverGroup) => serverGroup.name) ?? [],
      [TransformServerGroupsTableColumnKeys.ENVIRONMENT]:
        serverGroups.map((serverGroup) => serverGroup.environment) ?? [],
      [TransformServerGroupsTableColumnKeys.OWNER]:
        serverGroups.map((serverGroup) => serverGroup.metadata?.owner) ?? [],
      [TransformServerGroupsTableColumnKeys.CREATED_AT]:
        serverGroups.map((serverGroup) => serverGroup.created_at) ?? [],
      [TransformServerGroupsTableColumnKeys.LAST_UPDATED_AT]:
        serverGroups.map((serverGroup) => serverGroup.updated_at) ?? [],
    };
  }, [serverGroups]);

  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<
    TransformServerGroup,
    TransformServerGroupsTableColumnKeys,
    TransformServerGroupsTableFilterState
  >(defaultFilterState);

  const filterComponents: Record<
    TransformServerGroupsTableColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (entity: TransformServerGroup) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [TransformServerGroupsTableColumnKeys.ID]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return filterState.Status.includes(serverGroup.status ?? Status.UNSPECIFIED);
        },
        name: 'Status',
      },
      [TransformServerGroupsTableColumnKeys.STATUS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.Status}
            setSelected={(type) => handleFilterChange(filterComponents, 'Status', type)}
            items={uniq(
              serverGroups.map((serverGroup) => {
                const status = serverGroup.status ?? Status.UNSPECIFIED;
                return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
              })
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.Status.length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return filterState.Status.map((status) => status.toUpperCase()).includes(
            serverGroup.status ?? Status.UNSPECIFIED
          );
        },
        name: 'Status',
      },
      [TransformServerGroupsTableColumnKeys.NAME]: {
        filter: <>Filter</>,
        activeCount: () => filterState.Name.length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return filterState.Name.includes(serverGroup.name ?? '');
        },
        name: 'Name',
      },
      [TransformServerGroupsTableColumnKeys.ENVIRONMENT]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.Environment}
            setSelected={(type) => handleFilterChange(filterComponents, 'Environment', type)}
            items={uniq(serverGroups.map((serverGroup) => serverGroup.environment ?? '') ?? []).sort()}
            icon="Compute"
            title={''}
          />
        ),
        activeCount: () => filterState.Environment.length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return filterState.Environment.includes(serverGroup.environment ?? '');
        },
        name: 'Environment',
      },
      [TransformServerGroupsTableColumnKeys.OWNER]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.Owner}
            setSelected={(type) => handleFilterChange(filterComponents, 'Owner', type)}
            items={uniq(serverGroups.map((serverGroup) => serverGroup.metadata?.owner ?? '') ?? [])
              .filter((owner) => owner !== '')
              .sort()}
            icon="UserCircle"
            title={''}
          />
        ),
        activeCount: () => filterState.Owner.length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return filterState.Owner.includes(serverGroup.metadata?.owner ?? '');
        },
        name: 'Owner',
      },
      [TransformServerGroupsTableColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            minDate={defaultFilterState.Created[0]}
            maxDate={defaultFilterState.Created[1]}
            start={filterState['Created'][0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'Created', [date as Date, filterState['Created'][1] as Date]);
            }}
            end={filterState['Created'][1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'Created', [filterState['Created'][0] as Date, date as Date]);
            }}
          />
        ),
        activeCount: () => filterState.Created.length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return isInDateRange(new Date(serverGroup.created_at ?? ''), filterState.Created);
        },
        name: 'Created At',
      },
      [TransformServerGroupsTableColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            minDate={defaultFilterState['Last Updated'][0]}
            maxDate={defaultFilterState['Last Updated'][1]}
            start={filterState['Last Updated'][0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'Last Updated', [date as Date, filterState['Last Updated'][1]]);
            }}
            end={filterState['Last Updated'][1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'Last Updated', [filterState['Last Updated'][0], date as Date]);
            }}
          />
        ),
        activeCount: () => filterState['Last Updated'].length,
        filterCallback: (serverGroup: TransformServerGroup) => {
          return isInDateRange(new Date(serverGroup.updated_at ?? ''), filterState['Last Updated']);
        },
        name: 'Last Updated At',
      },
    };
  }, [defaultFilterState, filterState, handleFilterChange, serverGroups]);

  const columns: ExtendedTableColumn<TransformServerGroup & { id: string }>[] = [
    {
      name: 'Status',
      id: TransformServerGroupsTableColumnKeys.STATUS,
      columnWidth: 's',
      content: (serverGroup: TransformServerGroup) => {
        return <ServerGroupStatusBadge status={serverGroup.status ?? Status.UNSPECIFIED} />;
      },
      filterContent: filterComponents[TransformServerGroupsTableColumnKeys.STATUS].filter,
    },
    {
      name: 'Name',
      id: TransformServerGroupsTableColumnKeys.NAME,
      content: (serverGroup: TransformServerGroup) => {
        return (
          <Link
            icon="TransformServerGroup"
            name={serverGroup.name}
            url={AppRoutes.transformServerGroup
              .replace(':id', serverGroup.id ?? '')
              .replace(':workspace', workspace ?? '')
              .replace('*', 'metadata')}
          />
        );
      },
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, 'name', setSortType),
    },
    {
      name: 'Environment',
      id: TransformServerGroupsTableColumnKeys.ENVIRONMENT,
      content: (serverGroup: TransformServerGroup) => {
        if (!serverGroup.environment) {
          return <Text>-</Text>;
        }
        return (
          <Link
            icon="Compute"
            name={serverGroup.environment}
            onClick={() => {
              setFlyoutContent(serverGroup.environment);
            }}
          />
        );
      },
      ...tableStringSortParameters(sortType, 'environment', setSortType),
    },
    {
      name: 'ID',
      id: TransformServerGroupsTableColumnKeys.ID,
      content: (serverGroup: TransformServerGroup) => {
        return (
          <FlexGroup padding="0" gapSize="0.5" direction="row" alignItems="center">
            <FlexItem grow={0}>
              <Text color={theme.v1.colors.text.subduedText} code>
                {serverGroup.id}
              </Text>
            </FlexItem>
            <FlexItem grow={0}>
              <CopyButton code={serverGroup.id ?? ''} />
            </FlexItem>
          </FlexGroup>
        );
      },
      columnWidth: 'xl',
      ...tableStringSortParameters(sortType, 'id', setSortType),
    },
    {
      name: 'Owner',
      id: TransformServerGroupsTableColumnKeys.OWNER,
      content: (serverGroup: TransformServerGroup) => {
        if (!serverGroup.metadata?.owner) {
          return <>-</>;
        }
        return (
          <FlexGroup padding="0" alignItems="center" gapSize={'1'}>
            <Avatar name={serverGroup.metadata?.owner} />
            <FlexItem>{serverGroup.metadata?.owner}</FlexItem>
          </FlexGroup>
        );
      },
    },
    {
      name: 'Created',
      id: TransformServerGroupsTableColumnKeys.CREATED_AT,
      columnWidth: 'm',
      content: (serverGroup: TransformServerGroup) => {
        return <DateCell date={new Date(serverGroup.created_at ?? '')} />;
      },
    },
    {
      name: 'Last Updated',
      id: TransformServerGroupsTableColumnKeys.LAST_UPDATED_AT,
      columnWidth: 'm',
      content: (serverGroup: TransformServerGroup) => {
        return <DateCell date={new Date(serverGroup.updated_at ?? '')} />;
      },
    },
  ];

  const categories: ComboFilterCategory[] = [
    {
      name: 'Status',
      icon: 'AccountsAndAccess',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Status', defaultFilterState.Status);
      },
      popover: filterComponents[TransformServerGroupsTableColumnKeys.STATUS].filter,
    },
    {
      name: 'Environment',
      icon: 'Compute',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Environment', defaultFilterState.Environment);
      },
      popover: filterComponents[TransformServerGroupsTableColumnKeys.ENVIRONMENT].filter,
    },
    {
      name: 'Owner',
      icon: 'AccountsAndAccess',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Owner', defaultFilterState.Owner);
      },
      popover: filterComponents[TransformServerGroupsTableColumnKeys.OWNER].filter,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Created', defaultFilterState.Created);
      },
      popover: filterComponents[TransformServerGroupsTableColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Last Updated', defaultFilterState['Last Updated']);
      },
      popover: filterComponents[TransformServerGroupsTableColumnKeys.LAST_UPDATED_AT].filter,
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(serverGroups, activeFilters, ['name', 'id'], searchQuery ?? '', sortType);

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<TransformServerGroup & { id: string }> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as TransformServerGroupsTableColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<TransformServerGroup & { id: string }>[];

  return (
    <>
      <TableViewLayout
        table={
          <Table
            items={filteredResults.map((serverGroup) => ({ id: serverGroup.id ?? '', ...serverGroup }))}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={{
              title: 'No Transform Server Groups found',
              description: 'Create a Transform Server Group to get started',
              actions: [],
              placement: 'full-page',
              useCase: 'empty',
            }}
          />
        }
        searchInput={
          <SearchInput
            hasAnyFilters={hasAnyFilters}
            activeFilterCount={activeFilterCount}
            handleResetFilters={handleResetFilters}
            onSearchInputChange={onSearchInputChange}
            categories={categories}
          />
        }
        filterAndColumnControls={<ColumnsPopover columnsSelector={columnsSelector} />}
      />
      {flyoutContent !== undefined && (
        <EnvironmentFlyout
          environment={flyoutContent}
          closeCallback={() => {
            setFlyoutContent(undefined);
          }}
        />
      )}
    </>
  );
};

const TransformServerGroupsTableView: FC = () => {
  const { workspace } = useParams();
  const { data, isLoading, isError } = useGetTransformServerGroups(workspace ?? '');

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Empty
        placement="full-page"
        actions={[]}
        useCase="errorUnknown"
        title="Unknown Error"
        description="Error loading ingest server groups"
      />
    );
  }

  return <TransformServerGroupsTableContent serverGroups={data.transformServerGroups ?? []} />;
};

export default TransformServerGroupsTableView;
